import React, { useCallback } from 'react';

import Modal from '@amzn/awsui-components-console/modal';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import FormField from '@amzn/awsui-components-console/form-field';
import Input from '@amzn/awsui-components-console/input';

import { useTranslation } from 'src/i18n/shim/useTranslation';

/** Maximum characters for a saved search/filter name. */
export const MAX_SAVED_NAME_LENGTH = 50;

export interface SaveSearchModalProps {
  /** Whether the modal is visible. */
  readonly visible: boolean;
  /** Current name value in the text field. */
  readonly name: string;
  /** Callback when the name changes. */
  readonly onNameChange: (name: string) => void;
  /** Callback when the user confirms (clicks Save). */
  readonly onConfirm: () => void;
  /** Callback when the user cancels or dismisses. */
  readonly onCancel: () => void;
  /** Whether a save operation is in progress (shows loading spinner). */
  readonly isSaving?: boolean;
  /** Error text to display below the input (e.g. duplicate or failure message). */
  readonly errorText?: string;
  /** Modal header text. Defaults to the localized "Save search". */
  readonly header?: string;
  /** Placeholder text for the name input. Defaults to a localized prompt. */
  readonly placeholder?: string;
}

/**
 * Reusable modal for naming and saving a preference entry (saved search,
 * saved track filter, or any future preference type).
 *
 * Features:
 * - Character limit enforcement
 * - Save button disabled when name is empty or save is in progress
 * - Loading spinner during save
 * - Error text display for duplicates or failures
 */
const SaveSearchModal: React.FC<SaveSearchModalProps> = ({
  visible,
  name,
  onNameChange,
  onConfirm,
  onCancel,
  isSaving = false,
  errorText,
  header,
  placeholder,
}) => {
  const { t } = useTranslation();
  const isSaveDisabled = name.trim().length === 0;

  const handleChange = useCallback(
    (value: string) => onNameChange(value.slice(0, MAX_SAVED_NAME_LENGTH)),
    [onNameChange],
  );

  return (
    <Modal
      visible={visible}
      onDismiss={onCancel}
      header={header ?? t('save-search-header')}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onCancel}>
              {t('save-search-cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={onConfirm}
              disabled={isSaveDisabled || isSaving}
              loading={isSaving}
            >
              {t('save-search-save')}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <FormField
        label={t('save-search-name-label')}
        constraintText={t('save-search-char-count', {
          count: name.length,
          max: MAX_SAVED_NAME_LENGTH,
        })}
        errorText={errorText || undefined}
      >
        <Input
          value={name}
          onChange={({ detail }) => handleChange(detail.value)}
          placeholder={placeholder ?? t('save-search-placeholder')}
          ariaLabel={t('save-search-name-aria')}
        />
      </FormField>
    </Modal>
  );
};

export default SaveSearchModal;
